import { IdentityApiService } from '@api_patient/identity_api.service';
import { NotificationSettingsApiService } from '@api_patient/notification_settings_api.service';
import { AnalyticCategories } from '@application/analytic/analytic.service';
import { AuthService } from '@application/authentication/auth.service';
import { HttpStatusCode } from '@application/http.service';
import { HttpError } from '@application/http_error';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { withTranslation } from '@application/i18n/with_translation';
import { UniversalConfig } from '@application/universal_config.service';
import { toast } from '@cellula/react';
import { RouterService } from '@cortex-shim/router.service';
import { getAbsoluteRoute } from '@module/get_absolute_route';
import { signal } from '@preact/signals-react';
import { IdentityApiModel, NotificationComplianceSettingsApiModel } from '@visiba/api-vcs-client-v2';
import { ControllerProtocol, Injectable } from '@visiba/instantiation';
import { Scenario, ScenarioService } from './scenario.service';

@Injectable()
export class MyAccountController implements ControllerProtocol {
	public userData = signal<Nullable<MyAccountUserDataModel>>(null);
	public state = signal<MyAccountState>(MyAccountState.Ready);

	constructor(
		private readonly identityApiService: IdentityApiService,
		private readonly notificationSettingsApiService: NotificationSettingsApiService,
		private readonly authService: AuthService,
		private readonly routerService: RouterService,
		private readonly universalConfig: UniversalConfig,
		private readonly scenarioController: ScenarioService,
	) {}

	whenMount() {
		this.fetchData();
	}

	public async save(formData: Partial<MyAccountFormData>): Promise<boolean | void> {
		if (!this.userData.value?.identity) {
			throw new Error('No user identity found. Call this.load() before you call this method.');
		}

		if (
			!(await this.scenarioController.escalateScenarios(
				[
					new ScenarioWithoutNotifications(),
					new ScenarioEmailAndSmsNotificationsWithoutEmailAndMobileNumber(),
					new ScenarioSmsNotificationsWithoutMobileNumber(),
					new ScenarioEmailNotificationsWithoutEmail(),
				],
				formData,
			))
		) {
			return false;
		}

		// Save identity
		const saveIdentity = await this.identityApiService.update({
			Nationality: this.userData.value.identity.Nationality,
			FirstName: formData.firstName ? formData.firstName.trim() : null,
			LastName: formData.lastName ? formData.lastName.trim() : null,
			NationalIdentityNumber: formData.nin ?? null,
			Phone: formData.phone ? formData.phone.trim() : null,
			Email: formData.email ? formData.email.trim() : null,
		});

		if (saveIdentity.err) {
			throw saveIdentity.err;
		}

		// Save notification settings
		const notificationSettings = await this.notificationSettingsApiService.update({
			AllowEmail: formData.notificationsViaEmail ?? false,
			AllowSms: formData.notificationsViaSms ?? false,
		});

		if (notificationSettings.err) {
			throw notificationSettings.err;
		}

		// Fetch identity to update user information in other parts of the
		// application
		await this.authService.loadIdentity();

		return true;
	}

	public async delete(): Promise<void> {
		const returnUrl = getAbsoluteRoute(`${this.universalConfig.alias}?deleteAccountSuccess=true`);

		await this.identityApiService.deleteAccount();

		if (__visiba_feature_flags__['CareExperience.PW.Idp']) {
			window.location.href = this.identityApiService.getSignOutPath(returnUrl);

			return;
		}

		await this.authService.terminateIdentity();

		this.routerService.navigateByUrl(returnUrl);

		toast({
			label: withTranslation('PW.Feature.MyAccount.DeleteSuccess'),
		});
	}

	private async fetchData() {
		this.state.value = MyAccountState.Ready;

		const [identityResponse, notificationSettingsResponse, canDelete] = await Promise.all([
			this.identityApiService.get(),
			this.notificationSettingsApiService.get(),
			this.getDeletePermission(),
		]);

		if (identityResponse.err || notificationSettingsResponse.err || canDelete.err) {
			const errors = [identityResponse.err, notificationSettingsResponse.err, canDelete.err];

			this.state.value = errors.some((error) => error?.statusCode === HttpStatusCode.Unauthorized_401)
				? MyAccountState.Unauthorized
				: MyAccountState.Error;

			return;
		}

		this.userData.value = {
			identity: identityResponse.ok.data,
			notificationSettings: notificationSettingsResponse.ok.data,
			canDelete: canDelete.ok ?? false,
		};
	}

	private async getDeletePermission(): AsyncResult<boolean, HttpError<unknown>> {
		const result = await this.identityApiService.canDeleteAccount();
		if (result.err) {
			if (result.err.statusCode === 400) {
				return Ok(false);
			}

			return Err(result.err);
		}

		return Ok(true);
	}
}

export interface MyAccountFormData {
	nin: string | null;
	firstName: string | null;
	lastName: string | null;
	phone: string | null;
	email: string | null;
	notificationsViaSms: boolean | null;
	notificationsViaEmail: boolean | null;
}

interface MyAccountUserDataModel {
	identity: IdentityApiModel | null;
	notificationSettings: NotificationComplianceSettingsApiModel | null;
	canDelete: boolean;
}

export enum MyAccountState {
	Ready = 'Ready',
	Error = 'Error',
	Unauthorized = 'Unauthorized',
}

class ScenarioEmailAndSmsNotificationsWithoutEmailAndMobileNumber implements Scenario<MyAccountFormData> {
	readonly analyticQuirk_name = 'Sms notification without email and mobile number';
	readonly analyticQuirk_category = AnalyticCategories.MyAccount;
	readonly header = I18nAndL10n.translate('PW.Feature.MyAccount.NotificationsWithoutEmailAndMobileNumberDialog.Header');
	readonly description = I18nAndL10n.translate('PW.Feature.MyAccount.NotificationsWithoutEmailAndMobileNumberDialog.Description');
	readonly continue = I18nAndL10n.translate('PW.Feature.MyAccount.NotificationsWithoutEmailAndMobileNumberDialog.Continue');
	readonly abort = I18nAndL10n.translate('PW.Feature.MyAccount.NotificationsWithoutEmailAndMobileNumberDialog.Abort');

	escalation(data: MyAccountFormData) {
		if (data.notificationsViaEmail && !data.email && data.notificationsViaSms && !data.phone) {
			return true;
		}

		return false;
	}
}

class ScenarioSmsNotificationsWithoutMobileNumber implements Scenario<MyAccountFormData> {
	readonly analyticQuirk_name = 'Sms notification without mobile number';
	readonly analyticQuirk_category = AnalyticCategories.MyAccount;
	readonly header = I18nAndL10n.translate('PW.Feature.MyAccount.NotificationsWithoutMobileNumberDialog.Header');
	readonly description = I18nAndL10n.translate('PW.Feature.MyAccount.NotificationsWithoutMobileNumberDialog.Description');
	readonly continue = I18nAndL10n.translate('PW.Feature.MyAccount.NotificationsWithoutMobileNumberDialog.Continue');
	readonly abort = I18nAndL10n.translate('PW.Feature.MyAccount.NotificationsWithoutMobileNumberDialog.Abort');

	escalation(data: MyAccountFormData) {
		if (data.notificationsViaSms && !data.phone) {
			return true;
		}

		return false;
	}
}

class ScenarioEmailNotificationsWithoutEmail implements Scenario<MyAccountFormData> {
	readonly analyticQuirk_name = 'Sms notification without email';
	readonly analyticQuirk_category = AnalyticCategories.MyAccount;
	readonly header = I18nAndL10n.translate('PW.Feature.MyAccount.NotificationsWithoutEmailDialog.Header');
	readonly description = I18nAndL10n.translate('PW.Feature.MyAccount.NotificationsWithoutEmailDialog.Description');
	readonly continue = I18nAndL10n.translate('PW.Feature.MyAccount.NotificationsWithoutEmailDialog.Continue');
	readonly abort = I18nAndL10n.translate('PW.Feature.MyAccount.NotificationsWithoutEmailDialog.Abort');

	escalation(data: MyAccountFormData) {
		if (data.notificationsViaEmail && !data.email) {
			return true;
		}

		return false;
	}
}

class ScenarioWithoutNotifications implements Scenario<MyAccountFormData> {
	readonly analyticQuirk_name = 'Without notifications';
	readonly analyticQuirk_category = AnalyticCategories.MyAccount;
	readonly header = I18nAndL10n.translate('PW.Feature.MyAccount.WithoutNotificationsDialog.Header');
	readonly description = I18nAndL10n.translate('PW.Feature.MyAccount.WithoutNotificationsDialog.Description');
	readonly continue = I18nAndL10n.translate('PW.Feature.MyAccount.WithoutNotificationsDialog.Continue');
	readonly abort = I18nAndL10n.translate('PW.Feature.MyAccount.WithoutNotificationsDialog.Abort');

	escalation(data: MyAccountFormData) {
		if (!data.notificationsViaEmail && !data.notificationsViaSms) {
			return true;
		}

		return false;
	}
}
